import { announceStatus } from "ll-common-utilities";
/* ─── price-range ───────────────────────────────────────────────────── */
var PriceRange = class extends HTMLElement {
  #abortController;
  connectedCallback() {
    this.#abortController = new AbortController();
    const rangeLowerBound = this.querySelector('input[type="range"]:first-child'), rangeHigherBound = this.querySelector('input[type="range"]:last-child'), textInputLowerBound = this.querySelector('input[name="filter.v.price.gte"]'), textInputHigherBound = this.querySelector('input[name="filter.v.price.lte"]');
    textInputLowerBound.addEventListener("focus", () => textInputLowerBound.select(), { signal: this.#abortController.signal });
    textInputHigherBound.addEventListener("focus", () => textInputHigherBound.select(), { signal: this.#abortController.signal });
    textInputLowerBound.addEventListener("change", (event) => {
      event.preventDefault();
      event.target.value = Math.max(Math.min(parseInt(event.target.value), parseInt(textInputHigherBound.value || event.target.max) - 1), event.target.min);
      rangeLowerBound.value = event.target.value;
      rangeLowerBound.parentElement.style.setProperty("--range-min", `${parseInt(rangeLowerBound.value) / parseInt(rangeLowerBound.max) * 100}%`);
    }, { signal: this.#abortController.signal });
    textInputHigherBound.addEventListener("change", (event) => {
      event.preventDefault();
      event.target.value = Math.min(Math.max(parseInt(event.target.value), parseInt(textInputLowerBound.value || event.target.min) + 1), event.target.max);
      rangeHigherBound.value = event.target.value;
      rangeHigherBound.parentElement.style.setProperty("--range-max", `${parseInt(rangeHigherBound.value) / parseInt(rangeHigherBound.max) * 100}%`);
    }, { signal: this.#abortController.signal });
    rangeLowerBound.addEventListener("change", (event) => {
      event.stopPropagation();
      textInputLowerBound.value = event.target.value;
      textInputLowerBound.dispatchEvent(new Event("change", { bubbles: true }));
    }, { signal: this.#abortController.signal });
    rangeHigherBound.addEventListener("change", (event) => {
      event.stopPropagation();
      textInputHigherBound.value = event.target.value;
      textInputHigherBound.dispatchEvent(new Event("change", { bubbles: true }));
    }, { signal: this.#abortController.signal });
    rangeLowerBound.addEventListener("input", (event) => {
      event.target.value = Math.min(parseInt(event.target.value), parseInt(textInputHigherBound.value || event.target.max) - 1);
      event.target.parentElement.style.setProperty("--range-min", `${parseInt(event.target.value) / parseInt(event.target.max) * 100}%`);
      textInputLowerBound.value = event.target.value;
    }, { signal: this.#abortController.signal });
    rangeHigherBound.addEventListener("input", (event) => {
      event.target.value = Math.max(parseInt(event.target.value), parseInt(textInputLowerBound.value || event.target.min) + 1);
      event.target.parentElement.style.setProperty("--range-max", `${parseInt(event.target.value) / parseInt(event.target.max) * 100}%`);
      textInputHigherBound.value = event.target.value;
    }, { signal: this.#abortController.signal });
  }
  disconnectedCallback() {
    this.#abortController.abort();
  }
};
if (!window.customElements.get("price-range")) {
  window.customElements.define("price-range", PriceRange);
}
/* ─── quantity-selector ─────────────────────────────────────────────── */
var QuantitySelector = class extends HTMLElement {
  #abortController;
  #decreaseButton;
  #increaseButton;
  #inputElement;
  constructor() {
    super(...arguments);
  }
  connectedCallback() {
    this.#abortController = new AbortController();
    this.#decreaseButton = this.querySelector("button:first-of-type");
    this.#increaseButton = this.querySelector("button:last-of-type");
    this.#inputElement = this.querySelector("input");
    this.#decreaseButton?.addEventListener("click", this.#onDecreaseQuantity.bind(this), { signal: this.#abortController.signal });
    this.#increaseButton?.addEventListener("click", this.#onIncreaseQuantity.bind(this), { signal: this.#abortController.signal });
    this.#inputElement?.addEventListener("input", () => this.#updateUI(), { signal: this.#abortController.signal });
  }
  disconnectedCallback() {
    this.#abortController.abort();
  }
  get quantity() {
    return this.#inputElement.value;
  }
  set quantity(quantity) {
    this.#inputElement.value = quantity;
    this.#inputElement.dispatchEvent(new Event("change", { bubbles: true }));
    this.#updateUI();
  }
  restoreDefaultValue() {
    this.#inputElement.value = this.#inputElement.defaultValue;
    this.#updateUI();
  }
  #onDecreaseQuantity() {
    if (this.hasAttribute("allow-reset-to-zero") && this.#inputElement.value === this.#inputElement.min) {
      this.#inputElement.value = 0;
    } else {
      this.#inputElement.stepDown();
    }
    announceStatus(this.#inputElement.value);
    this.#inputElement.dispatchEvent(new Event("change", { bubbles: true }));
    this.#updateUI();
  }
  #onIncreaseQuantity() {
    this.#inputElement.stepUp();
    announceStatus(this.#inputElement.value);
    this.#inputElement.dispatchEvent(new Event("change", { bubbles: true }));
    this.#updateUI();
  }
  #updateUI() {
    if (this.#decreaseButton) {
      if (this.hasAttribute("allow-reset-to-zero") && this.#inputElement.value === this.#inputElement.min) {
        this.#decreaseButton.disabled = false;
      } else {
        this.#decreaseButton.disabled = parseInt(this.#inputElement.value) <= parseInt(this.#inputElement.min);
      }
    }
    if (this.#increaseButton) {
      this.#increaseButton.disabled = this.#inputElement.hasAttribute("max") ? parseInt(this.#inputElement.value) >= parseInt(this.#inputElement.max) : false;
    }
  }
};
var QuantityInput = class extends HTMLElement {
  constructor() {
    super();
    this.#inputElement.addEventListener("input", this.#onValueInput.bind(this));
    this.#inputElement.addEventListener("change", this.#onValueChange.bind(this));
    this.#inputElement.addEventListener("focus", () => this.#inputElement.select());
  }
  connectedCallback() {
    this.style.setProperty("--quantity-selector-character-count", `${this.#inputElement.value.length}ch`);
  }
  get quantity() {
    return parseInt(this.#inputElement.value);
  }
  get #inputElement() {
    return this.firstElementChild;
  }
  #onValueInput() {
    if (this.#inputElement.value === "") {
      this.#inputElement.value = this.#inputElement.min || 1;
    }
    this.style.setProperty("--quantity-selector-character-count", `${this.#inputElement.value.length}ch`);
  }
  #onValueChange() {
    if (!this.#inputElement.checkValidity()) {
      this.#inputElement.stepDown();
    }
  }
};
if (!window.customElements.get("quantity-selector")) {
  window.customElements.define("quantity-selector", QuantitySelector);
}
if (!window.customElements.get("quantity-input")) {
  window.customElements.define("quantity-input", QuantityInput);
}
